"use client";

import { useEffect, useMemo } from "react";
import { resolveLiteralBoolean, resolvePath, resolveStringArray, resolveUnknown, useSeedHasSelection } from "./binding";
import { isSelectionNormalized, normalizeSelection, resolveSelectionItems, resolveSelectionRules, toggleSelectionValue } from "./selectionRules";
import type { MacaronRenderContext, RecordAny } from "./types";

export function MacaronOrderedSelectionList({ config, ctx }: { config: RecordAny; ctx: MacaronRenderContext }) {
  const items = useMemo(() => resolveSelectionItems(config.items, ctx.data), [config.items, ctx.data]);
  const values = useMemo(() => items.map((item) => item.value), [items]);
  const rules = useMemo(() => resolveSelectionRules(config, items.length), [config, items.length]);
  const selectionPath = resolvePath(config.selection);
  const hasSelectionPath = resolvePath(config.hasSelection);
  const hasSelectionLiteral = resolveLiteralBoolean(config.hasSelection);
  const rawSelectionValue = resolveUnknown(config.selection, ctx.data);
  const rawSelection = Array.isArray(rawSelectionValue) ? rawSelectionValue : null;
  const selected = useMemo(() => normalizeSelection(resolveStringArray(config.selection, ctx.data), values, rules.maxSelection), [config.selection, ctx.data, rules.maxSelection, values]);
  const isFull = selected.length >= rules.maxSelection;
  const updateData = ctx.updateData;

  useSeedHasSelection(hasSelectionPath, hasSelectionLiteral, ctx.data, updateData);
  useEffect(() => {
    if (!hasSelectionPath) return;
    updateData?.(hasSelectionPath, selected.length >= rules.requiredSelection);
  }, [hasSelectionPath, rules.requiredSelection, selected, updateData]);
  useEffect(() => {
    if (!selectionPath || isSelectionNormalized(rawSelection, selected)) return;
    updateData?.(selectionPath, selected);
  }, [rawSelection, selected, selectionPath, updateData]);

  return (
    <div className="flex flex-col gap-2">
      {items.map((item) => {
        const order = selected.indexOf(item.value);
        const active = order >= 0;
        const disabled = !active && isFull && rules.maxSelection > 1;
        return (
          <button
            key={item.value}
            type="button"
            className={`flex items-center justify-between rounded-lg border px-3 py-2 text-sm ${active ? "border-[#8CA62A] bg-[#F6FAE8]" : "border-[#D0D5DD] bg-white"} ${disabled ? "opacity-40" : ""}`}
            disabled={!selectionPath || disabled}
            onClick={() => {
              if (!selectionPath) return;
              const next = toggleSelectionValue(selected, item.value, rules.maxSelection);
              if (next.length === selected.length && next.every((entry, index) => entry === selected[index])) return;
              ctx.updateData?.(selectionPath, next);
              if (hasSelectionPath) ctx.updateData?.(hasSelectionPath, next.length >= rules.requiredSelection);
            }}
          >
            <div className="min-w-0 flex-1">{ctx.renderChild(item.child)}</div>
            {active ? <span className="rounded-full bg-[#111827] px-2 py-0.5 text-xs text-white">{order + 1}</span> : <span aria-hidden className="h-px w-3 rounded-full bg-[#111827]/16" />}
          </button>
        );
      })}
    </div>
  );
}
